import type { SanityImageSource } from '@sanity/image-url';
import { createImageUrlBuilder } from '@sanity/image-url';
import { dataset, projectId } from '@/lib/sanity/core/config';
import type { MediaDto } from '@/lib/sanity/dto';

// Plain project coordinates, not the `client` instance: Client Components import
// the helpers below, and passing the client would ship `createClient` with them.
const builder = createImageUrlBuilder({ projectId, dataset });

/** Quality of URLs the browser downloads as they are. */
const IMAGE_QUALITY = 80;

/**
 * Quality of the CDN source next/image re-encodes. Those URLs are an intermediate
 * the optimizer fetches server-side once, never a file the browser downloads, so
 * they stay near-lossless: compressing at 80 here and again in the optimizer costs
 * quality twice for the same delivered bytes.
 */
const OPTIMIZER_SOURCE_QUALITY = 90;

export function urlForImage(source: SanityImageSource) {
    return builder.image(source);
}

export function sanityImageSection(img?: MediaDto, maxWidth = 1920) {
    return img?.asset
        ? builder.image(img).width(maxWidth).quality(IMAGE_QUALITY).auto('format').fit('max').url()
        : undefined;
}

export const sanityImage = {
    og: (img?: MediaDto) =>
        img?.asset ? builder.image(img).width(1200).height(630).fit('max').quality(IMAGE_QUALITY).url() : undefined,

    // Source for any in-page content image (block panels, cards, portraits).
    // next/image re-encodes it, so it stays near-lossless here.
    content: (img?: MediaDto) =>
        img?.asset
            ? builder.image(img).width(1800).quality(OPTIMIZER_SOURCE_QUALITY).auto('format').fit('max').url()
            : undefined,
};

export type SanityImageVariant = keyof typeof sanityImage;
